import React from "react";

import dayjs from "dayjs";

import styles from "./ChildCard.module.scss";
import { formatTime } from "../../../utils/timeFunctions";

// Status types for dashboard view
export type ChildStatus =
  | "bookings"
  | "inSession"
  | "signedOut"
  | "atEca"
  | "returning"
  | "notArrived"
  | "absent"
  | "attended";

export interface IChildCardData {
  /** Unique identifier for the child */
  childId: number;
  /** Booking identifier */
  bookingId: number;
  /** Child's first name */
  firstName?: string;
  /** Child's last name */
  lastName?: string;
  /** Child's gender (M/F) */
  gender?: string;
  /** Parent/guardian's first name */
  parentFirstName?: string;
  /** Parent/guardian's last name */
  parentLastName?: string;
  /** Primary service name - shown when different from current service */
  primaryServiceName?: string;
  /** Event type from session events (for dashboard status) */
  eventType?: string;
  /** Event time (sign in/out time) */
  eventTime?: dayjs.Dayjs;
}

interface IChildCardProps {
  /** Child data to display */
  child: IChildCardData;
  /** Whether to show the status badge and time (dashboard mode) */
  showStatus?: boolean;
  /** Whether to show the gender badge (bookings mode) */
  showGender?: boolean;
  /** Whether the card has recently changed (for animation) */
  isChanged?: boolean;
}

/**
 * Get child status from event type
 */
export const getChildStatus = (eventType: string | undefined): ChildStatus => {
  if (!eventType) return "notArrived";

  switch (eventType) {
    case "ChildSignedInEventType":
      return "inSession";
    case "ChildExternalCheckOut":
      return "returning";
    case "ChildMarkedAsAbsentEventType":
      return "absent";
    case "ChildMarkedAsAtECAEvent":
      return "atEca";
    case "ChildSignedOutEventType":
      return "signedOut";
    default:
      return "notArrived";
  }
};

/**
 * Get display label for a status
 */
export const getStatusLabel = (status: ChildStatus): string => {
  switch (status) {
    case "bookings":
      return "Bookings";
    case "inSession":
      return "In Session";
    case "signedOut":
      return "Signed Out";
    case "atEca":
      return "At ECA";
    case "returning":
      return "Returning";
    case "notArrived":
      return "To Be Signed In";
    case "absent":
      return "Absent";
    case "attended":
      return "Attended";
  }
};

/**
 * Get the full name of a child
 */
export const getChildFullName = (child: IChildCardData): string => {
  return `${child.firstName ?? ""} ${child.lastName ?? ""}`.trim();
};

/**
 * Get the parent's full name
 */
export const getParentFullName = (child: IChildCardData): string => {
  return `${child.parentFirstName ?? ""} ${child.parentLastName ?? ""}`.trim();
};

/**
 * Get child initials for the avatar
 */
export const getChildInitials = (child: IChildCardData): string => {
  const firstInitial = child.firstName?.charAt(0)?.toUpperCase() ?? "";
  const lastInitial = child.lastName?.charAt(0)?.toUpperCase() ?? "";
  return `${firstInitial}${lastInitial}`;
};

/**
 * Get avatar CSS class based on status
 */
const getAvatarClass = (status: ChildStatus): string => {
  switch (status) {
    case "inSession":
      return styles.childAvatarInSession;
    case "signedOut":
      return styles.childAvatarSignedOut;
    case "atEca":
      return styles.childAvatarAtEca;
    case "returning":
      return styles.childAvatarReturning;
    case "absent":
      return styles.childAvatarAbsent;
    case "attended":
      return styles.childAvatarAttended;
    case "notArrived":
    default:
      return styles.childAvatarNotArrived;
  }
};

/**
 * Get status badge CSS class based on status
 */
const getBadgeClass = (status: ChildStatus): string => {
  switch (status) {
    case "inSession":
      return styles.childStatusBadgeInSession;
    case "signedOut":
      return styles.childStatusBadgeSignedOut;
    case "atEca":
      return styles.childStatusBadgeAtEca;
    case "returning":
      return styles.childStatusBadgeReturning;
    case "absent":
      return styles.childStatusBadgeAbsent;
    case "attended":
      return styles.childStatusBadgeAttended;
    case "notArrived":
    default:
      return styles.childStatusBadgeNotArrived;
  }
};

/**
 * Get time prefix based on status
 */
const getTimePrefix = (status: ChildStatus): string => {
  switch (status) {
    case "inSession":
      return "IN:";
    case "signedOut":
      return "OUT:";
    default:
      return "";
  }
};

/**
 * Reusable child card component for displaying child information
 * Used in both Dashboard and Bookings pages
 */
export const ChildCard: React.FC<IChildCardProps> = ({
  child,
  showStatus = false,
  showGender = false,
  isChanged = false,
}) => {
  const status = showStatus ? getChildStatus(child.eventType) : "bookings";
  const parentName = getParentFullName(child);

  return (
    <div className={`${styles.childCard} ${isChanged ? styles.childCardChanged : ""}`}>
      <div className={styles.childCardContent}>
        <div className={styles.childCardLeft}>
          <div className={`${styles.childAvatar} ${showStatus ? getAvatarClass(status) : ""}`}>
            {getChildInitials(child)}
          </div>
          <div className={styles.childInfo}>
            <div className={styles.childName}>{getChildFullName(child)}</div>
            {parentName && <div className={styles.childSubInfo}>{parentName}</div>}
            {child.primaryServiceName && <div className={styles.childPrimaryService}>{child.primaryServiceName}</div>}
          </div>
        </div>
        <div className={styles.childCardRight}>
          {showStatus && (
            <>
              <span className={`${styles.childStatusBadge} ${getBadgeClass(status)}`}>{getStatusLabel(status)}</span>
              {child.eventTime && (
                <span className={styles.childStatusTime}>
                  {getTimePrefix(status)} {formatTime(child.eventTime)}
                </span>
              )}
            </>
          )}
          {showGender && child.gender && (
            <span className={`${styles.genderBadge} ${child.gender === "M" ? styles.genderMale : styles.genderFemale}`}>
              {child.gender}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChildCard;
